import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { entityApi } from '../../lib/api';
import api from '../../lib/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import {
  ArrowLeft, FileText, CreditCard, PenTool, Building2, MapPin,
  Calendar, Shield, Loader2, ExternalLink, RefreshCcw, CheckCircle2, AlertTriangle, Clock,
} from 'lucide-react';
import { getEntityTypeColor } from '../../lib/utils';
import { toast } from 'sonner';
import { useCart } from '../../context/CartContext';

const STATUS_BADGE = {
  active: { Icon: CheckCircle2, label: 'Active / Good Standing', className: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  pending: { Icon: Clock, label: 'Pending — Filing Submitted', className: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  non_compliant: { Icon: AlertTriangle, label: 'Non-Compliant', className: 'text-red-400 bg-red-500/10 border-red-500/30' },
  dissolved: { Icon: AlertTriangle, label: 'Dissolved', className: 'text-slate-400 bg-slate-500/10 border-slate-500/30' },
  unknown: { Icon: Clock, label: 'Not Filed Yet', className: 'text-slate-400 bg-slate-500/10 border-slate-500/30' },
};

export default function EntityDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, items: cartItems } = useCart();
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [complianceEvents, setComplianceEvents] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [registering, setRegistering] = useState(false);

  const loadEntity = useCallback(async () => {
    try {
      const res = await entityApi.getOne(id);
      setEntity(res.data);
      try {
        const ev = await api.get(`/entities/${id}/compliance-events`);
        setComplianceEvents(ev.data?.events || []);
      } catch (_) { /* not yet filed — fine */ }
    } catch (err) {
      console.error('Failed to load entity:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadEntity(); }, [loadEntity]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post(`/entities/${id}/sync`);
      toast.success(
        res.data.previous_status === res.data.current_status
          ? `Status confirmed: ${res.data.current_status}`
          : `Status changed: ${res.data.previous_status} → ${res.data.current_status}`,
      );
      await loadEntity();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleRegister = async () => {
    setRegistering(true);
    try {
      // Fetch the state filing quote and add it to the cart
      const quoteRes = await api.get(`/entities/${id}/state-filing-quote`);
      const q = quoteRes.data;
      const alreadyInCart = cartItems.some(
        (it) => it.service_id === 'state_filing_fee' && it.entity_id === id,
      );
      if (alreadyInCart) {
        toast.info('Already in your cart — taking you there.', {
          action: { label: 'Open cart', onClick: () => navigate('/cart') },
        });
      } else {
        addItem('state_filing_fee', 1, {
          entityId: id,
          entityName: q.entity_name,
          state: q.state,
          priceUsd: q.fee_usd,
          priceUsdc: q.fee_usdc,
        });
        toast.success(
          `${q.state} filing fee — $${q.fee_usd} added to cart. Pay to submit.`,
          { action: { label: 'Open cart', onClick: () => navigate('/cart') } },
        );
      }
      // Auto-navigate to cart for one-click flow
      navigate('/cart');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load filing quote');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!entity) {
    return (
      <DashboardLayout>
        <div className="stat-card text-center py-12">
          <h3 className="text-xl font-semibold mb-2">Entity Not Found</h3>
          <p className="text-slate-400 mb-6">The entity you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/entities')} className="gradient-accent">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Entities
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const colors = getEntityTypeColor(entity.type);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/entities')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold font-['Outfit']">{entity.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors.bg} ${colors.text}`}>
                  {entity.type}
                </span>
                <span className="text-slate-400 text-sm flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {entity.jurisdiction}
                </span>
              </div>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/entities/${id}/edit`)}
            variant="outline"
            className="border-slate-700"
          >
            Edit Entity
          </Button>
        </div>

        {/* Entity Info */}
        <div className="stat-card">
          <h2 className="text-xl font-semibold mb-4">Entity Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-slate-400 mb-1">Entity Type</p>
              <p className="font-medium">{entity.type}</p>
            </div>
            <div>
              <p className="text-sm text-slate-400 mb-1">Jurisdiction</p>
              <p className="font-medium">{entity.jurisdiction}</p>
            </div>
            {entity.parent_id && (
              <div>
                <p className="text-sm text-slate-400 mb-1">Parent Entity</p>
                <p className="font-medium">{entity.parent_name || 'N/A'}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-400 mb-1">Created</p>
              <p className="font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(entity.created_at).toLocaleDateString()}
              </p>
            </div>
            
            {/* EIN Information */}
            {entity.ein && (
              <div>
                <p className="text-sm text-slate-400 mb-1">EIN</p>
                <p className="font-medium font-mono">{entity.ein}</p>
              </div>
            )}
            {entity.ein_status && entity.ein_status !== 'not_applied' && (
              <div>
                <p className="text-sm text-slate-400 mb-1">EIN Status</p>
                <p className="font-medium">
                  <span className={`px-2 py-1 rounded text-xs ${
                    entity.ein_status === 'approved' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {entity.ein_status === 'approved' ? 'Approved' : 
                     entity.ein_status === 'pending' ? 'Pending' : 'Not Applied'}
                  </span>
                </p>
              </div>
            )}
          </div>
          
          {entity.description && (
            <div className="mt-6">
              <p className="text-sm text-slate-400 mb-1">Description</p>
              <p className="text-slate-300">{entity.description}</p>
            </div>
          )}
        </div>

        {/* State Filing & Compliance */}
        <div className="stat-card" data-testid="state-filing-panel">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">State Filing & Compliance</h2>
                <p className="text-xs text-slate-500">
                  Provider: <span className="font-mono">{entity.state_filing_provider || 'not connected'}</span>
                  {entity.last_synced_at && <> · synced {new Date(entity.last_synced_at).toLocaleString()}</>}
                </p>
              </div>
            </div>
            {entity.provider_entity_id ? (
              <Button
                variant="outline" size="sm"
                onClick={handleSync}
                disabled={syncing}
                data-testid="entity-sync-btn"
              >
                <RefreshCcw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Sync status
              </Button>
            ) : (
              <Button
                onClick={handleRegister}
                disabled={registering}
                className="gradient-accent"
                data-testid="entity-register-btn"
              >
                {registering ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                File & pay
              </Button>
            )}
          </div>

          {/* Status badge + filing info grid */}
          {(() => {
            const statusKey = entity.compliance_status || 'unknown';
            const cfg = STATUS_BADGE[statusKey] || STATUS_BADGE.unknown;
            const SIcon = cfg.Icon;
            return (
              <div className="grid md:grid-cols-3 gap-4 mb-5">
                <div className={`rounded-lg border px-4 py-3 ${cfg.className}`} data-testid="entity-status-badge">
                  <div className="flex items-center gap-2 mb-1">
                    <SIcon className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wide">Status</span>
                  </div>
                  <div className="font-semibold">{cfg.label}</div>
                </div>
                <div className="rounded-lg border border-slate-800 px-4 py-3 bg-slate-950/50">
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Provider Entity ID</div>
                  <div className="font-mono text-sm" data-testid="entity-provider-id">
                    {entity.provider_entity_id || <span className="text-slate-600">—</span>}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-800 px-4 py-3 bg-slate-950/50">
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">State Filing #</div>
                  <div className="font-mono text-sm">
                    {entity.state_filing_number || <span className="text-slate-600">—</span>}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Compliance events */}
          {complianceEvents.length > 0 ? (
            <div className="border-t border-slate-800 pt-5" data-testid="entity-compliance-events">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm uppercase tracking-wide text-slate-400">Upcoming Compliance Events</h3>
                <span className="text-xs text-slate-500">{complianceEvents.length} pending</span>
              </div>
              <div className="space-y-2">
                {complianceEvents.map((ev) => (
                  <div
                    key={ev.id}
                    data-testid={`compliance-event-${ev.id}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-950/50 border border-slate-800"
                  >
                    <Calendar className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium capitalize">{ev.event_type.replace(/_/g, ' ')}</div>
                      {ev.description && <div className="text-xs text-slate-400">{ev.description}</div>}
                    </div>
                    <div className="text-right hidden sm:block">
                      <div className="text-xs text-slate-400">Due</div>
                      <div className="text-sm font-mono">{ev.due_date}</div>
                    </div>
                    {ev.amount_due_usd > 0 && (
                      <div className="text-right">
                        <div className="text-xs text-slate-400">Fee</div>
                        <div className="text-sm font-semibold">${ev.amount_due_usd.toFixed(2)}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : entity.provider_entity_id && (
            <div className="text-center text-sm text-slate-500 py-4 border-t border-slate-800">
              No upcoming compliance events.
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Filing & Compliance */}
          <div className="stat-card hover:bg-slate-800/50 transition-all cursor-pointer group">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-all">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <h3 className="font-semibold mb-2">Filing & Compliance</h3>
            <p className="text-sm text-slate-400 mb-4">
              Submit formation documents and manage ongoing compliance requirements
            </p>
            <Button 
              className="w-full gradient-accent"
              onClick={() => navigate(`/entities/${id}/filing`)}
            >
              Start Filing
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Payments */}
          <div className="stat-card hover:bg-slate-800/50 transition-all cursor-pointer group">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-all">
                <CreditCard className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
            <h3 className="font-semibold mb-2">Fees & Payments</h3>
            <p className="text-sm text-slate-400 mb-4">
              Pay formation fees, annual reports, and other entity-related costs
            </p>
            <Button 
              className="w-full gradient-accent"
              onClick={() => navigate('/payments')}
            >
              Make Payment
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* DocuSign */}
          <div className="stat-card hover:bg-slate-800/50 transition-all cursor-pointer group">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center group-hover:bg-purple-500/30 transition-all">
                <PenTool className="w-6 h-6 text-purple-400" />
              </div>
            </div>
            <h3 className="font-semibold mb-2">Document Signing</h3>
            <p className="text-sm text-slate-400 mb-4">
              Sign formation documents, operating agreements, and legal paperwork
            </p>
            <Button 
              className="w-full gradient-accent"
              onClick={() => navigate(`/entities/${id}/docusign`)}
            >
              Sign Documents
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>

        {/* Audit & Certification */}
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-amber-400" />
            <h2 className="text-xl font-semibold">Sentinel Audit</h2>
          </div>
          <p className="text-slate-400 mb-4">
            Run a comprehensive audit on this entity to identify risks and receive compliance recommendations.
          </p>
          <Button
            onClick={() => navigate('/audit-certification')}
            className="gradient-accent"
          >
            Run Audit
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
